import { FormField } from '@/components/FormField';
import { Switch } from '@/components/ui/switch';
import { OptionSelect } from '@/components/OptionSelect';
import { BranchPatternInput } from './BranchPatternInput';
import { FORKS_LABEL } from './environmentHelpers';
import { withProtected } from './environmentFormModel';
import type { EnvironmentFormApi } from './useEnvironmentForm';

type FormProp = { form: EnvironmentFormApi };

export function ProtectionField({ form }: FormProp) {
  const { values, edit } = form;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span id="env-form-protected-label" className="text-[13px] font-medium text-foreground">
        Protection status
      </span>
      <label className="flex min-h-9 cursor-pointer items-center gap-3 max-sm:min-h-11">
        <Switch data-testid="env-form-protected" checked={values.isProtected} onCheckedChange={(on) => edit((v) => withProtected(v, on))} aria-labelledby="env-form-protected-label" aria-describedby="env-form-protected-hint" />
        <span className="text-[13px] text-foreground">Protected environment</span>
      </label>
      <span id="env-form-protected-hint" className="text-xs text-subtle">
        Only the listed branches build here, never every branch, and forks are always blocked
      </span>
    </div>
  );
}

/** The allowed branch patterns as chips; a protected environment needs at least one and never `*`. */
export function PatternsField({ form }: FormProp) {
  const { values, edit, errorFor } = form;
  const error = errorFor('allowedBranchPatterns');
  return (
    <FormField id="env-form-allowedBranchPatterns" label="Allowed branch patterns" required={values.isProtected} hint="* is the only wildcard (release/* matches release/1.4). Empty on an unprotected environment: any branch." error={error}>
      <BranchPatternInput
        id="env-form-allowedBranchPatterns"
        value={values.patterns}
        onChange={(patterns) => edit((v) => ({ ...v, patterns }), 'allowedBranchPatterns')}
        draft={values.draft}
        onDraftChange={(draft) => edit((v) => ({ ...v, draft }))}
        invalid={Boolean(error)}
        describedBy={error ? 'env-form-allowedBranchPatterns-error' : 'env-form-allowedBranchPatterns-hint'}
      />
    </FormField>
  );
}

/** Forks and untrusted refs: always blocked (and locked) on a protected environment. */
export function ForksField({ form }: FormProp) {
  const { values, edit, errorFor } = form;
  const error = errorFor('untrustedRefsBlocked');
  return (
    <FormField id="env-form-untrustedRefsBlocked" label="Forks and untrusted refs" hint={values.isProtected ? 'Always blocked on a protected environment' : 'Builds from forks and external pull requests'} error={error} className="sm:max-w-md">
      <OptionSelect
        id="env-form-untrustedRefsBlocked"
        data-testid="env-form-forks"
        value={values.blocked ? 'blocked' : 'allowed'}
        disabled={values.isProtected}
        onValueChange={(picked) => edit((v) => ({ ...v, blocked: picked === 'blocked' }))}
        aria-describedby={error ? 'env-form-untrustedRefsBlocked-error' : 'env-form-untrustedRefsBlocked-hint'}
        options={[
          { value: 'blocked', label: FORKS_LABEL.blocked },
          { value: 'allowed', label: FORKS_LABEL.allowed },
        ]}
      />
    </FormField>
  );
}
